import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

export interface PermissionSection {
  title: string;
  permissions: RawPermission[];
}

interface SelectedPermissionsResponse {
  role: string;
  permissions: SelectedPermissions[];
}

interface SelectedPermissions {
  id: string;
  name: string;
  desc: string | null;
  active: number;
}

interface RoleState {
  roles: Array<Role> | null;
  selectedRole: Role | null;
  setRole: (role: Role) => void;
  setRoleById: (id: string) => void;
  permissions: Array<PermissionSection> | null;
  getPermissions: (roleId?: string) => Promise<void>;
  getRoles: () => Promise<void>;
  savePermissions: (permission: Array<PermissionSection>) => Promise<void>;
  setPermissions: (permission: Array<PermissionSection>) => void;
}

export interface RawPermission {
  id: string;
  name: string;
  desc: string | null;
  checked: boolean;
}

interface Role {
  id: string;
  name: string;
  scope: "global" | "media";
  active: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface RoleResponse {
  success: boolean;
  message: string;
  data: Array<Role>;
}

export interface PermissionsResponse {
  success: boolean;
  message: string;
  data: {
    [section: string]: RawPermission[];
  };
}

const useRoleStore = create<RoleState>((set, get) => ({
  roles: [],
  selectedRole: null,
  permissions: null,
  setPermissions: (permission) => {
    set({ permissions: permission });
  },
  setRole: (role: Role) => {
    set({ selectedRole: role });
  },
  setRoleById: (id: string) => {
    if (get().roles.length != 0) {
      const currentRole = get().roles.find((e) => e.id == id);
      set({ selectedRole: currentRole });
    }
  },
  getPermissions: async (roleId?: string) => {
    try {
      const id = roleId || get().selectedRole?.id;
      if (!id) {
        throw new Error("No role selected");
      }
      const response = await apiClient.get<PermissionsResponse>("/permissions");
      const response1 = await apiClient.get<SelectedPermissionsResponse>(
        `/roles/${id}/permissions`
      );
      const data = response.data.data;
      const transformed = Object.entries(data).map(([title, permissions]) => ({
        title,
        permissions,
      }));
      transformed.map((e) =>
        e.permissions.map((ee) => {
          ee.checked = response1.data.permissions.find(
            (perm) => perm.name == ee.name
          )
            ? true
            : false;
        })
      );
      set({ permissions: transformed });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
  getRoles: async () => {
    try {
      const response = await apiClient.get<RoleResponse>("/roles");
      const data = response.data.data;
      set({ roles: data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
  savePermissions: async (permission: Array<PermissionSection>) => {
    try {
      const selectedPermissions = permission.flatMap((section) =>
        section.permissions
          .filter((perm) => perm.checked)
          .map((perm) => perm.name)
      );
      const response = await apiClient.post(
        `/roles/${get().selectedRole.id}/permissions`,
        {
          permissions: selectedPermissions,
        }
      );
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useRoleStore;
